/**
 * Stage 3 — Review.
 *
 * Editable table view: vendor, invoice #, date, amount, currency, coding, status.
 * Click a row → card view. Inline-edit any text cell.
 *
 * Coding column shows a gold pill (when coded) or muted "uncoded" tag.
 * "Code uncoded vendors" runs the coding modal walkthrough.
 */

import { getState, setState, updateFile } from '../state.js';
import { el, clear, toast } from './dom.js';
import { saveBatchSnapshot } from '../persistence.js';
import { startCodingWalkthrough } from './stage-code.js';
import { fileIsUncoded, distributionTotalDelta } from '../lib/rules.js';
import { openRulesEditor } from './rules-editor.js';

let mounted = null;
const expandedRows = new Set();
const selectedLineByFileId = new Map();

export function renderReviewStage(mount) {
  clear(mount);
  mounted = mount;
  const state = getState();
  const wrap = el('div');

  const extracted = state.files.filter(f => f.status === 'done' || f.status === 'failed');
  const ready  = state.files.filter(f => f.status === 'done').length;
  const failed = state.files.filter(f => f.status === 'failed').length;
  const uncoded = state.files.filter(f => f.status === 'done' && fileIsUncoded(f)).length;

  wrap.appendChild(el('div', { class: 'path-bar' }, [
    el('div', {}, [
      el('span', { class: 'label' }, ['Review']),
      el('span', { class: 'path' }, [state.workName || '']),
      el('span', { class: 'meta' }, [
        ` · ${ready} extracted${failed ? `, ${failed} failed` : ''}`
      ])
    ]),
    el('div', { class: 'actions' }, [
      el('button', { class: 'ghost-btn', onClick: () => setState({ stage: 'extract' }) }, ['← Back to extract'])
    ])
  ]));

  if (extracted.length === 0) {
    wrap.appendChild(el('div', { class: 'panel panel-empty' }, [
      el('h2', {}, ['Nothing extracted yet']),
      el('p', {}, ['Run extraction first.'])
    ]));
    mount.appendChild(wrap);
    return;
  }

  wrap.appendChild(renderTable(state));

  wrap.appendChild(el('div', { class: 'batch-summary' }, [
    el('div', { class: 'summary-counts' }, [
      el('div', { class: 'count' }, [
        el('span', { class: 'num' }, [String(ready)]),
        el('span', { class: 'lbl' }, ['ready'])
      ]),
      uncoded > 0 && el('div', { class: 'count' }, [
        el('span', { class: 'num', style: { color: 'var(--gold-deep)' } }, [String(uncoded)]),
        el('span', { class: 'lbl' }, ['uncoded'])
      ]),
      failed > 0 && el('div', { class: 'count' }, [
        el('span', { class: 'num', style: { color: 'var(--danger)' } }, [String(failed)]),
        el('span', { class: 'lbl' }, ['failed'])
      ])
    ]),
    el('div', { class: 'summary-actions' }, [
      uncoded > 0 && el('button', {
        class: 'ghost-btn',
        onClick: () => startCodingWalkthrough(() => renderReviewStage(mounted))
      }, ['Code uncoded vendors']),
      el('button', {
        class: 'primary-btn',
        disabled: ready === 0,
        onClick: () => setState({ stage: 'export' })
      }, ['Continue to export →'])
    ])
  ]));

  mount.appendChild(wrap);
}

function renderTable(state) {
  const tableWrap = el('div', { class: 'review-table-wrap' });
  const table = el('table', { class: 'review' });

  const thead = el('thead');
  const trh = el('tr');
  for (const [cls, label] of [
    ['col-expand',   ''],
    ['col-vendor',   'Vendor'],
    ['col-invoice',  'Invoice #'],
    ['col-date',     'Date'],
    ['col-amount',   'Amount'],
    ['col-currency', 'Cur'],
    ['col-coding',   'Coding'],
    ['col-status',   'Status']
  ]) {
    trh.appendChild(el('th', { class: cls + (cls === 'col-amount' ? ' amount' : '') }, [label]));
  }
  thead.appendChild(trh);
  table.appendChild(thead);

  const tbody = el('tbody');
  state.files.forEach((f, idx) => {
    if (f.status !== 'done' && f.status !== 'failed') return;
    tbody.appendChild(renderRow(f, idx));
    if (expandedRows.has(f.id) && f.status === 'done') {
      for (const ctr of renderChildRows(f)) tbody.appendChild(ctr);
    }
  });
  table.appendChild(tbody);
  tableWrap.appendChild(table);
  return tableWrap;
}

function distKey(line) {
  if (line.kind === 'tax') return `t${line.taxIndex ?? 0}`;
  if (line.kind === 'item') return `i${line.lineIndex ?? 0}`;
  return 's0';
}

function renderChildRows(f) {
  const lines = (f.distribution && f.distribution.lines) || [];
  const edits = f.distributionEdits || {};
  const rows = [];
  if (!lines.length) return rows;
  const selectedKey = selectedLineByFileId.get(f.id);
  for (const line of lines) {
    const key = distKey(line);
    const ov = edits[key] || {};
    const gl = ov.glCode != null ? ov.glCode : line.glCode;
    const pr = ov.propertyCode != null ? ov.propertyCode : line.propertyCode;
    const isItem = line.kind === 'item';
    const isSelected = isItem && selectedKey === key;
    const tr = el('tr', {
      class: `review-child${isSelected ? ' review-child-selected' : ''}`,
      onClick: (ev) => {
        if (!isItem) return;
        if (ev.target.closest('input, button')) return;
        selectedLineByFileId.set(f.id, key);
        renderReviewStage(mounted);
      }
    });
    tr.appendChild(el('td'));
    tr.appendChild(el('td', { colspan: 2, style: { fontSize: '12px', color: 'var(--text-secondary)' } }, [
      (line.kind === 'tax' ? '[Tax] ' : '[Line] ') + (line.description || ''),
      isSelected ? el('span', { class: 'review-line-selected-badge' }, ['Selected for rule']) : null
    ]));
    tr.appendChild(el('td'));
    const amt = typeof line.amount === 'number' ? line.amount.toFixed(2) : '';
    tr.appendChild(el('td', { class: 'amount' }, [amt]));
    tr.appendChild(el('td'));
    tr.appendChild(el('td', {}, [childGlEditor(f, key, gl, pr)]));
    tr.appendChild(el('td', { style: { fontSize: '11px', color: 'var(--text-tertiary)' } }, [line.source || '']));
    rows.push(tr);
  }
  return rows;
}

function childGlEditor(f, key, gl, pr) {
  const wrap = el('div', { style: { display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' } });
  const gIn = el('input', { type: 'text', class: 'input-mono', style: { width: '90px', fontSize: '12px' }, value: gl || '' });
  const pIn = el('input', { type: 'text', class: 'input-mono', style: { width: '76px', fontSize: '12px' }, value: pr || '' });
  const save = () => {
    const next = { ...(f.distributionEdits || {}) };
    const ng = gIn.value.trim();
    const np = pIn.value.trim();
    if (!ng && !np) delete next[key];
    else next[key] = { glCode: ng || undefined, propertyCode: np || undefined };
    updateFile(f.id, { distributionEdits: Object.keys(next).length ? next : null });
    saveBatchSnapshot().catch(() => {});
  };
  gIn.addEventListener('change', save);
  pIn.addEventListener('change', save);
  wrap.appendChild(gIn);
  wrap.appendChild(pIn);
  return wrap;
}

function getEffectiveValue(f, field) {
  if (f.edits && f.edits[field] != null) return f.edits[field];
  return f.extracted ? f.extracted[field] : null;
}

function setEdit(f, field, value) {
  const edits = { ...(f.edits || {}), [field]: value };
  // If matches the original, drop the override
  if (f.extracted && f.extracted[field] === value) delete edits[field];
  updateFile(f.id, { edits });
  saveBatchSnapshot().catch(() => {});
}

function renderRow(f, idx) {
  const tr = el('tr', {
    class: f.status === 'failed' ? 'row-failed' : (rowIsFlagged(f) ? 'row-flagged' : ''),
    onClick: (ev) => {
      if (ev.target.closest('input, button')) return;
      setState({ stage: 'card', cardIndex: idx });
    }
  });

  if (f.status === 'failed') {
    tr.appendChild(el('td', { colspan: 8 }, [
      el('span', { class: 'badge failed', style: { marginRight: '8px', verticalAlign: 'middle' } }, [
        el('span', { class: 'dot' }), 'Failed'
      ]),
      el('span', { style: { color: 'var(--danger-fg)', verticalAlign: 'middle' } }, [
        `${f.name} — ${f.error || 'extraction failed'}`
      ])
    ]));
    return tr;
  }

  const exp = el('td', { class: 'col-expand' });
  const hasDist = f.distribution && Array.isArray(f.distribution.lines) && f.distribution.lines.length;
  const toggle = el('button', {
    class: 'ghost-btn',
    style: { padding: '2px 8px', fontSize: '12px' },
    title: hasDist ? 'Show line items / tax' : 'Show export preview',
    onClick: (ev) => {
      ev.stopPropagation();
      if (expandedRows.has(f.id)) expandedRows.delete(f.id);
      else expandedRows.add(f.id);
      renderReviewStage(mounted);
    }
  }, [expandedRows.has(f.id) ? '▾' : '▸']);
  exp.appendChild(toggle);
  tr.appendChild(exp);

  // Vendor
  tr.appendChild(editableTd(f, 'vendor_name', 'col-vendor', false));
  // Invoice #
  tr.appendChild(editableTd(f, 'invoice_number', 'col-invoice', false));
  // Date
  tr.appendChild(editableTd(f, 'invoice_date', 'col-date', false));
  // Amount (+ reconciliation hint for detail mode)
  const amtTd = editableTd(f, 'total_amount', 'col-amount amount', true);
  const delta = distributionTotalDelta(f);
  if (delta != null) {
    amtTd.appendChild(el('div', {
      class: 'help',
      style: { color: 'var(--warn)', fontSize: '11px', marginTop: '2px' }
    }, [`⚠ off by ${delta.toFixed(2)}`]));
  }
  tr.appendChild(amtTd);
  // Currency
  tr.appendChild(editableTd(f, 'currency', 'col-currency', false));

  // Coding cell
  tr.appendChild(el('td', { class: 'col-coding' }, [renderCodingCell(f)]));

  // Status
  const isMissing = isMissingCriticalField(f);
  tr.appendChild(el('td', { class: 'col-status' }, [
    isMissing
      ? el('span', { class: 'badge flagged' }, [el('span', { class: 'dot' }), 'Needs attention'])
      : el('span', { class: 'badge done' }, [el('span', { class: 'dot' }), 'Ready'])
  ]));

  return tr;
}

function rowIsFlagged(f) {
  return isMissingCriticalField(f);
}

function isMissingCriticalField(f) {
  if (f.status !== 'done') return false;
  const need = ['vendor_name', 'invoice_number', 'invoice_date', 'total_amount'];
  for (const k of need) {
    const v = getEffectiveValue(f, k);
    if (v == null || v === '') return true;
  }
  return false;
}

function editableTd(f, field, cls, isAmount) {
  const v = getEffectiveValue(f, field);
  const display = formatForDisplay(v, isAmount);

  const td = el('td', { class: cls });
  if (display == null || display === '') {
    td.appendChild(el('span', { class: 'cell-missing cell-warning' }, ['—']));
  } else {
    td.appendChild(document.createTextNode(display));
  }
  td.title = String(v ?? '');

  // Click to edit
  td.addEventListener('dblclick', (ev) => {
    ev.stopPropagation();
    enterEdit(f, field, td, isAmount);
  });
  // Single click is reserved for opening the card view via row.onClick.
  // To make editing discoverable, also accept a long-press? — skipping for v1; dblclick is documented in guide.

  return td;
}

function enterEdit(f, field, td, isAmount) {
  const cur = getEffectiveValue(f, field);
  const input = el('input', {
    type: 'text',
    class: `cell-edit${isAmount ? ' amount' : ''}`,
    value: cur == null ? '' : String(cur)
  });
  clear(td);
  td.appendChild(input);
  input.focus();
  input.select();

  const commit = () => {
    let raw = input.value.trim();
    let newValue = raw === '' ? null : raw;
    if (isAmount && newValue != null) {
      const n = Number(newValue);
      if (!Number.isFinite(n)) {
        toast('Amount must be a number.', 'error');
        return;
      }
      newValue = n;
    }
    setEdit(f, field, newValue);
    // Re-render the entire row by re-rendering review — simpler than surgical updates
    renderReviewStage(mounted);
  };
  const cancel = () => renderReviewStage(mounted);

  input.addEventListener('blur', commit);
  input.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') { ev.preventDefault(); commit(); }
    else if (ev.key === 'Escape') { ev.preventDefault(); cancel(); }
  });
}

function formatForDisplay(v, isAmount) {
  if (v == null || v === '') return null;
  if (isAmount) {
    if (typeof v === 'number') return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return String(v);
  }
  return String(v);
}

function renderCodingCell(f) {
  const c = f.coding;
  const wrap = el('div', { class: 'review-coding-cell' });
  if (!c || (!c.glCode && !c.propertyCode)) {
    wrap.appendChild(el('span', { class: 'badge uncoded' }, ['uncoded']));
    return wrap;
  }
  const src = c.source === 'rule' ? 'rule' : c.source === 'manual' ? 'manual' : 'set';
  const badge = el('span', { class: `review-source-badge review-source-${src}` }, [src]);
  const pill = el('span', {
    class: 'coding-pill',
    title: c.source === 'rule'
      ? `Applied by rule (${(c.score != null ? Number(c.score).toFixed(2) : '1.00')})`
      : c.source === 'manual' ? 'Manual override' : 'Coding'
  }, [
    c.glCode || '—',
    el('span', { class: 'sep' }, [' / ']),
    c.propertyCode || '—'
  ]);
  if (c.detailMode) {
    pill.appendChild(el('span', { class: 'review-mode-tag', title: 'Detail / distribution export' }, [' · detail']));
  }
  wrap.appendChild(badge);
  wrap.appendChild(pill);

  const vendor = getEffectiveValue(f, 'vendor_name');
  if (c.source === 'manual' && vendor) {
    wrap.appendChild(el('button', {
      class: 'ghost-btn',
      style: { fontSize: '11px', padding: '2px 6px', marginTop: '4px', display: 'block' },
      onClick: (ev) => {
        ev.stopPropagation();
        openRulesEditor(() => renderReviewStage(mounted), {
          vendorMatch: String(vendor).trim(),
          glCodeDefault: (c.glCode || '').trim(),
          propertyCodeDefault: (c.propertyCode || '').trim(),
          taxGl: (c.glCode || '').trim(),
          emitDistributionLines: !!c.detailMode,
          lineItemRules: []
        });
      }
    }, ['Save as rule…']));
  }
  if (vendor && f.status === 'done') {
    const hasSelectedLine = !!getSelectedItemLine(f);
    const isExpanded = expandedRows.has(f.id);
    wrap.appendChild(el('div', {
      class: `review-rule-hint${!isExpanded ? ' clickable' : ''}`,
      title: !isExpanded ? 'Click to expand row and choose a line item' : '',
      onClick: (ev) => {
        ev.stopPropagation();
        if (isExpanded) return;
        expandedRows.add(f.id);
        renderReviewStage(mounted);
      }
    }, [
      hasSelectedLine
        ? 'Line selected below. You can add a rule from it.'
        : 'Tip: expand row and select a [Line] row below to enable Add rule.'
    ]));

    wrap.appendChild(el('button', {
      class: 'ghost-btn',
      style: { fontSize: '11px', padding: '2px 6px', marginTop: '2px', display: 'block' },
      title: hasSelectedLine ? 'Create a rule from selected line item' : 'Expand row and select a [Line] row first',
      disabled: !hasSelectedLine,
      onClick: (ev) => {
        ev.stopPropagation();
        addRuleFromSelectedLine(f);
      }
    }, ['Add rule from selected line…']));

    wrap.appendChild(el('button', {
      class: 'ghost-btn',
      style: { fontSize: '11px', padding: '2px 6px', marginTop: '2px', display: 'block' },
      onClick: (ev) => {
        ev.stopPropagation();
        openRulesEditor(
          () => renderReviewStage(mounted),
          null,
          { openEditRuleId: c?.ruleId || null }
        );
      }
    }, ['Edit rules…']));
  }
  return wrap;
}

function getSelectedItemLine(f) {
  const lines = Array.isArray(f.distribution?.lines) ? f.distribution.lines : [];
  const selectedKey = selectedLineByFileId.get(f.id);
  if (!selectedKey) return null;
  const line = lines.find(l => l.kind === 'item' && distKey(l) === selectedKey);
  if (!line) return null;
  return { key: selectedKey, line };
}

function lineItemFromDistributionLine(f, distLine) {
  if (distLine?.kind !== 'item') return null;
  const e = { ...f.extracted, ...(f.edits || {}) };
  const items = Array.isArray(e.line_items) ? e.line_items : [];
  const idx = Number(distLine.lineIndex);
  if (!Number.isInteger(idx) || idx < 0 || idx >= items.length) return null;
  return items[idx];
}

function chooseMatchMode(item) {
  const itemCode = item?.item_code != null ? String(item.item_code).trim() : '';
  if (itemCode) {
    const useItemCode = confirm(
      'Create line-item rule using Item Code?\n\n' +
      'OK = Item Code match\n' +
      'Cancel = Keyword match from description'
    );
    return useItemCode ? 'itemCode' : 'keyword';
  }
  const useKeyword = confirm(
    'Selected line has no Item Code.\n\n' +
    'Create keyword-based rule from description instead?\n\n' +
    'OK = Create keyword rule\n' +
    'Cancel = Abort'
  );
  return useKeyword ? 'keyword' : null;
}

function buildDraftFromLineSelection(f, distLine, item, matchOn) {
  const coding = f.coding || {};
  const edits = f.distributionEdits || {};
  const ov = edits[distKey(distLine)] || {};
  const lineGl = (ov.glCode != null ? ov.glCode : distLine.glCode) || '';
  const lineProperty = ov.propertyCode != null ? ov.propertyCode : distLine.propertyCode;
  const description = item?.description != null ? String(item.description).trim() : '';
  const itemCode = item?.item_code != null ? String(item.item_code).trim() : '';
  const matchValue = matchOn === 'itemCode' ? itemCode : description;
  if (!matchValue) return null;

  return {
    vendorMatch: String(getEffectiveValue(f, 'vendor_name') || '').trim(),
    glCodeDefault: String(coding.glCode || lineGl || '').trim(),
    propertyCodeDefault: String(coding.propertyCode || lineProperty || '').trim(),
    taxGl: String(coding.glCode || lineGl || '').trim(),
    emitDistributionLines: true,
    lineItemRules: [{
      matchOn,
      matchValue,
      glCode: String(lineGl || coding.glCode || '').trim(),
      propertyCode: lineProperty != null && String(lineProperty).trim() !== ''
        ? String(lineProperty).trim()
        : null
    }],
    note: ''
  };
}

function addRuleFromSelectedLine(f) {
  const selected = getSelectedItemLine(f);
  if (!selected) {
    toast('Select a line item in the expanded row first.', 'error');
    return;
  }
  const item = lineItemFromDistributionLine(f, selected.line);
  if (!item) {
    toast('Selected line item data is unavailable.', 'error');
    return;
  }

  const matchOn = chooseMatchMode(item);
  if (!matchOn) return;

  const draft = buildDraftFromLineSelection(f, selected.line, item, matchOn);
  if (!draft || !draft.vendorMatch) {
    toast('Could not build a rule from the selected line.', 'error');
    return;
  }
  if (!draft.glCodeDefault && !draft.propertyCodeDefault) {
    toast('Set GL or property coding before creating a rule.', 'error');
    return;
  }

  openRulesEditor(
    () => renderReviewStage(mounted),
    draft,
    { createThenEditFromDraft: true }
  );
}
